import { isVideoExportRecordingActive } from "@/animation/video-generator/exportActivity";
import { QueryKey } from "@/api";
import { useProfileAndTabParams } from "@/hooks";
import { useDateTimeFromUrl } from "@/hooks/useDateTimeFromUrl";
import { objectifyStateVal } from "@/models/state-machine-utils/stateMachineUtils";
import {
  setCurrentAnimationPrefetchContext,
  syncCurrentAnimationPrefetchContextForTimeState,
} from "@/models/time-control/animationPrefetchContext";
import type { AnimationCtx, TimeContextUnion } from "@/models/time-control/timeStateCtxTypes";
import { TimeEventType } from "@/models/time-control/timeStateEvents";
import { defaultTimeStateMachine } from "@/models/time-control/timeStateMachine";
import { type TimeEventUnion, type TimeStateMachine, TimeStates } from "@/models/time-control/timeStateTypes";
import { setTelemetryAnimationActive } from "@/otel/animation-state";
import ErrorPage from "@/pages/error";
import reduxStore from "@/reducer";
import { useQuery } from "@tanstack/react-query";
import { useInterpret } from "@xstate/react";
import type { DateTime } from "luxon";
import { type PropsWithChildren, createContext, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { type InterpreterFrom, State } from "xstate";
import { setAnimationPlaying, setAnimationSliderScrubbing, setCurrentAnimationFps } from "./animationPlaybackActivity";
import { readTimeStateDesc, saveTimeStateDesc, saveTimeStateDescDebounced } from "./timeStateApiHandlers";

export const TimeStateMachineContext = createContext<StateMachineContext>({
  timeStateService: {} as InterpreterFrom<TimeStateMachine>,
});

type StateMachineContext = {
  timeStateService: InterpreterFrom<TimeStateMachine>;
};

export const TimeMachineProvider = (props: PropsWithChildren<any>) => {
  const { profileId } = useProfileAndTabParams();
  const { data, isLoading, error } = useQuery([QueryKey.TIME_STATE, profileId], () => readTimeStateDesc(profileId));

  if (error) {
    return <ErrorPage error={error} />;
  }
  if (isLoading || !data) {
    return <></>;
  }

  return (
    <ActorProvider key={profileId} {...data}>
      {props.children}
    </ActorProvider>
  );
};

export const ActorProvider = ({
  stateValue,
  context,
  children,
}: PropsWithChildren & {
  stateValue: string;
  context: TimeContextUnion;
}) => {
  const { profileId } = useProfileAndTabParams();
  const profile = reduxStore.getState().profile?.profiles?.[profileId];
  const isLocked = "locked" in profile && profile.locked === true;
  const datetimeFromUrl = useDateTimeFromUrl();
  const updatedContext = useMemo(() => overwriteDateTime(context, datetimeFromUrl), [context, datetimeFromUrl]);

  const timeStateService = useInterpret(defaultTimeStateMachine, {
    devTools: process.env.NODE_ENV !== "production",
    state: State.from<TimeContextUnion, TimeEventUnion>(
      objectifyStateVal(stateValue),
      updatedContext as TimeContextUnion,
    ),
  });
  useInitStateTrigger(stateValue, timeStateService.send);

  const wasPlayingRef = useRef(false);

  useLayoutEffect(() => {
    const sync = (state: Parameters<typeof syncCurrentAnimationPrefetchContextForTimeState>[0]) => {
      syncCurrentAnimationPrefetchContextForTimeState(state);
    };

    sync(timeStateService.getSnapshot());
    const sub = timeStateService.subscribe(sync);
    return () => {
      setCurrentAnimationPrefetchContext(null);
      sub.unsubscribe();
    };
  }, [timeStateService]);

  useEffect(() => {
    const sub = timeStateService.subscribe((state) => {
      const isAnimation = state.matches(TimeStates.ANIMATION);
      const isPlaying = isAnimation && (state.context as AnimationCtx)?.modeOpts?.isPaused === false;
      setCurrentAnimationFps(isAnimation ? (state.context as AnimationCtx)?.modeOpts?.fps ?? null : null);
      setAnimationPlaying(isPlaying);
      setTelemetryAnimationActive(isAnimation);

      // If profile is locked, do not persist time state
      if (isLocked) {
        return;
      }

      if (isVideoExportRecordingActive()) {
        saveTimeStateDescDebounced.cancel();
        wasPlayingRef.current = isPlaying;
        return;
      }

      // On rising edge of Play: save immediately
      if (isPlaying && !wasPlayingRef.current) {
        saveTimeStateDesc(profileId, state)?.catch((e) => console.error(e));
      }

      // While NOT playing (paused, scrub, config changes, auto-refresh, etc.), use debounced saves
      if (!isPlaying) {
        saveTimeStateDescDebounced(profileId, state)?.catch((e) => console.error(e));
      }

      // Update ref after handling
      wasPlayingRef.current = isPlaying;
    });
    return () => {
      setCurrentAnimationFps(null);
      setAnimationPlaying(false);
      setAnimationSliderScrubbing(false);
      setTelemetryAnimationActive(false);
      saveTimeStateDescDebounced.flush();
      saveTimeStateDescDebounced.cancel();
      sub.unsubscribe();
    };
  }, [isLocked, timeStateService, profileId]);

  return <TimeStateMachineContext.Provider value={{ timeStateService }}>{children}</TimeStateMachineContext.Provider>;
};

/**
 * When initializing the state directly from the database, such as:
 *
 * const timeStateService = useInterpret(defaultTimeStateMachine, {
 *     devTools: true,
 *     state: State.from(objectifyStateVal(stateValue), context),
 * });
 *
 * This approach does not trigger lifecycle effects like entry, after, or exit events.
 *
 * To ensure that these effects are triggered (such as after a page refresh or on initial load),
 * it is necessary to manually trigger the initial state using an action.
 * By dispatching the same state through an action, it behaves like a transition, and the associated effects are invoked.
 *
 * For more details on state re-entry and transitions, refer to the documentation:
 * https://stately.ai/docs/transitions#re-entering
 *
 */
function useInitStateTrigger(stateValue: string, send: InterpreterFrom<TimeStateMachine>["send"]) {
  useEffect(() => {
    if (stateValue === TimeStates.AUTO_REFRESH) {
      // AUTO_REFRESH requires an explicit init event, because State.from(...)
      // does not run entry/after actions automatically.
      send({ type: TimeEventType.initAutoRefreshMode });
    } else if (stateValue === TimeStates.ANIMATION) {
      // Same reasoning for ANIMATION: ensure the machine runs through a transition
      // so entry actions can set up start/end times and snap the context correctly.
      send({ type: TimeEventType.initAnimationMode });
    }
  }, [stateValue, send]);
}
function overwriteDateTime(context: TimeContextUnion, newTime: DateTime | null) {
  return newTime
    ? {
        ...context,
        displayTime: newTime,
        nextDisplayTime_: newTime,
        modeOpts:
          "fps" in context.modeOpts
            ? { ...context.modeOpts } // AnimationCtx
            : { ...context.modeOpts, isLocked: true }, // AutoRefreshCtx
      }
    : context;
}
